/**
 * Simple obfuscation for GraphQL queries to prevent casual inspection.
 * This is NOT cryptographic security - just adds friction for competitors.
 */

export class QueryObfuscator {
  private static readonly MARKER = "O_";
  
  /**
   * Obfuscate a GraphQL query string for transmission
   */
  static obfuscate(query: string): string {
    if (!query) return query;
    
    // Convert to base64
    const base64 = Buffer.from(query, 'utf-8').toString('base64');
    
    // Simple character rotation to make it less obvious it's base64
    const rotated = base64
      .split('')
      .map(char => {
        const code = char.charCodeAt(0);
        // Rotate alphanumeric characters
        if (code >= 65 && code <= 90) { // A-Z
          return String.fromCharCode(((code - 65 + 13) % 26) + 65);
        } else if (code >= 97 && code <= 122) { // a-z
          return String.fromCharCode(((code - 97 + 13) % 26) + 97);
        } else if (code >= 48 && code <= 57) { // 0-9
          return String.fromCharCode(((code - 48 + 5) % 10) + 48);
        }
        return char;
      })
      .join('');
    
    // Add marker so we know it's obfuscated
    return this.MARKER + rotated;
  }
  
  /**
   * Deobfuscate a GraphQL query string
   */
  static deobfuscate(obfuscated: string): string {
    if (!obfuscated || !obfuscated.startsWith(this.MARKER)) {
      return obfuscated; // Not obfuscated, return as-is
    }
    
    // Remove marker
    const withoutMarker = obfuscated.substring(this.MARKER.length);
    
    // Reverse character rotation
    const unrotated = withoutMarker
      .split('')
      .map(char => {
        const code = char.charCodeAt(0);
        // Reverse rotate alphanumeric characters
        if (code >= 65 && code <= 90) { // A-Z
          return String.fromCharCode(((code - 65 - 13 + 26) % 26) + 65);
        } else if (code >= 97 && code <= 122) { // a-z
          return String.fromCharCode(((code - 97 - 13 + 26) % 26) + 97);
        } else if (code >= 48 && code <= 57) { // 0-9
          return String.fromCharCode(((code - 48 - 5 + 10) % 10) + 48);
        }
        return char;
      })
      .join('');
    
    // Decode from base64
    try {
      return Buffer.from(unrotated, 'base64').toString('utf-8');
    } catch (error) {
      console.error('Failed to deobfuscate query:', error);
      return obfuscated; // Return original if decoding fails
    }
  }
  
  /**
   * Check if a string is obfuscated
   */
  static isObfuscated(str: string): boolean {
    return str?.startsWith(this.MARKER) || false;
  }
}

// Export for CommonJS compatibility
export default QueryObfuscator;